<template>
  <b-modal :active.sync="isActive" has-modal-card trap-focus @close="$emit('close')">
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Autorizar ampliación</p>
        <button type="button" class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <b-message v-if="contract.metrics" type="is-warning" has-icon>
          Al autorizar una nueva ampliación del mismo tipo, si existe una ampliación anterior, esta dejará de estar activa.
        </b-message>
        <b-message v-if="vigenciaInsufficientMessage" type="is-danger" has-icon>
          {{ vigenciaInsufficientMessage }}
        </b-message>
        <p class="mb-3"><strong>{{ contract.folio }}</strong> — {{ contract.organismo_nombre || contract.empresa_nombre }}</p>
        <b-field
          label="Tipo de ampliación"
          :type="errors.tipo ? 'is-danger' : null"
          :message="errors.tipo"
        >
          <b-select v-model="form.tipo" expanded :disabled="availableTypes.length < 2">
            <option v-for="tipo in availableTypes" :key="tipo" :value="tipo">{{ typeLabel(tipo) }}</option>
          </b-select>
        </b-field>
        <b-field
          label="Folio del contrato"
          :type="errors.folio_contrato ? 'is-danger' : null"
          :message="errors.folio_contrato"
        >
          <b-input v-model="form.folio_contrato" readonly></b-input>
        </b-field>
        <b-field
          label="Número de convenio modificatorio"
          :type="errors.numero_convenio_modificatorio ? 'is-danger' : null"
          :message="errors.numero_convenio_modificatorio"
        >
          <b-input v-model="form.numero_convenio_modificatorio" placeholder="Ej. CONV-2026-001"></b-input>
        </b-field>
        <b-field label="Porcentaje de ampliación" :loading="loadingPorcentajes">
          <b-input :value="porcentajeDisplay" readonly></b-input>
        </b-field>
      </section>
      <footer class="modal-card-foot">
        <button class="button" @click="close">Cerrar</button>
        <button class="button is-primary" :loading="loading" :disabled="!!vigenciaInsufficientMessage" @click="submit">Autorizar</button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { availableExtensionTypes, EXTENSION_TYPE_LABELS, vigenciaExtensionInsufficientMessage } from './extensionNeeds';

export default {
  name: 'ContractAmpliacionModal',
  props: {
    active: Boolean,
    contract: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      isActive: this.active,
      loading: false,
      loadingPorcentajes: false,
      errors: {},
      porcentajes: {
        presupuesto: null,
        vigencia: null
      },
      form: {
        tipo: 'presupuesto',
        folio_contrato: '',
        numero_convenio_modificatorio: ''
      }
    };
  },
  computed: {
    availableTypes() {
      return availableExtensionTypes(this.contract, false);
    },
    porcentajeDisplay() {
      const value = this.form.tipo === 'vigencia'
        ? this.porcentajes.vigencia
        : this.porcentajes.presupuesto;

      if (value == null || this.loadingPorcentajes) {
        return '';
      }

      return `${value}%`;
    },
    vigenciaInsufficientMessage() {
      if (this.form.tipo !== 'vigencia') {
        return '';
      }
      return vigenciaExtensionInsufficientMessage(this.contract, this.porcentajes.vigencia);
    }
  },
  watch: {
    active(val) {
      this.isActive = val;
      if (val) {
        this.resetForm();
        this.loadPorcentajes();
      }
    },
    isActive(val) { if (!val) this.$emit('close'); }
  },
  methods: {
    resetForm() {
      this.errors = {};
      this.form = {
        tipo: this.availableTypes[0] || 'presupuesto',
        folio_contrato: this.contract.folio || '',
        numero_convenio_modificatorio: ''
      };
    },
    typeLabel(tipo) {
      return EXTENSION_TYPE_LABELS[tipo] || tipo;
    },
    async loadPorcentajes() {
      const fromContract = this.contract.ampliacion_porcentajes;
      if (fromContract?.presupuesto != null && fromContract?.vigencia != null) {
        this.porcentajes = { ...fromContract };
        return;
      }

      this.loadingPorcentajes = true;
      try {
        const { data } = await this.$http.get('budget-control/ampliacion-porcentajes');
        this.porcentajes = {
          presupuesto: data.presupuesto,
          vigencia: data.vigencia
        };
      } catch (e) {
        this.porcentajes = { presupuesto: 20, vigencia: 20 };
      } finally {
        this.loadingPorcentajes = false;
      }
    },
    close() {
      this.isActive = false;
      this.errors = {};
    },
    validateForm() {
      this.errors = {};
      let valid = true;

      if (!this.form.tipo || !this.availableTypes.includes(this.form.tipo)) {
        this.errors.tipo = 'Seleccione el tipo de ampliación.';
        valid = false;
      }

      if (!(this.form.folio_contrato || '').trim()) {
        this.errors.folio_contrato = 'El folio del contrato es obligatorio.';
        valid = false;
      } else if (this.contract.folio && this.form.folio_contrato.trim() !== this.contract.folio) {
        this.errors.folio_contrato = 'El folio no coincide con el contrato seleccionado.';
        valid = false;
      }

      if (!(this.form.numero_convenio_modificatorio || '').trim()) {
        this.errors.numero_convenio_modificatorio = 'Capture el número de convenio modificatorio.';
        valid = false;
      }

      return valid;
    },
    applyServerErrors(response) {
      const serverErrors = response?.data?.errors || {};
      Object.keys(serverErrors).forEach((key) => {
        const message = serverErrors[key];
        this.errors[key] = Array.isArray(message) ? message[0] : message;
      });
    },
    async submit() {
      if (!this.validateForm()) {
        this.$toast.error('Revise los datos de la ampliación.');
        return;
      }
      if (this.vigenciaInsufficientMessage) {
        this.errors.tipo = this.vigenciaInsufficientMessage;
        this.$toast.error(this.vigenciaInsufficientMessage);
        return;
      }

      this.loading = true;
      try {
        const payload = {
          tipo: this.form.tipo,
          folio_contrato: this.form.folio_contrato.trim(),
          numero_convenio_modificatorio: this.form.numero_convenio_modificatorio.trim()
        };
        const { data } = await this.$http.post(`budget-control/contracts/${this.contract.id}/ampliaciones`, payload);
        this.$toast.success(data.message || 'Ampliación autorizada');
        this.$emit('saved');
        this.close();
      } catch ({ response }) {
        this.applyServerErrors(response);
        const serverErrors = response?.data?.errors || {};
        const detail = Object.values(serverErrors).flat().join(' ');
        this.$toast.error(detail || response?.data?.message || 'No se pudo autorizar la ampliación.');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
